import { FC } from 'react';

import WaveUIProgressDialog from '@/shared/components/dialogs/WaveUIProgressDialog';
import WaveUINotificationBar from '@/shared/components/utilities/WaveUINotificationBar';
import useModalDialog from '@/shared/hooks/useModalDialog';
import useNotificationBar from '@/shared/hooks/useNotificationBar';
import useProgressDialog from '@/shared/hooks/useProgressDialog';
import DialogName from '@/shared/types/dialogName.enum';
import ShippingProvider from '@/shared/types/shipping/shippingProvider.type';

import ManageShippingProviderDialog from './components/ManageShippingProviderDialog';
import ShippingProvidersTable_v2 from './components/ShippingProvidersTable_v2/ShippingProvidersTable_v2';
import useFetchProviderKeys from './hooks/useFetchProviderKeys';
import useManageShippingProviderDialog from './hooks/useManageShippingProviderDialog';

const ShippingProviders: FC = () => {
  const { dialog, onUpdateDialogState } = useModalDialog<
    ShippingProvider,
    DialogName
  >();
  const { progressDialog, onUpdateProgressDialog } = useProgressDialog();
  const { notificationBar, onUpdateNotificationBar } = useNotificationBar();

  const { shippingProviderKeys } = useFetchProviderKeys();

  const {
    refreshTableIndex,
    handleCreateShippingProvider,
    handleUpdateShippingProvider,
  } = useManageShippingProviderDialog({
    onUpdateDialogState,
    onUpdateNotificationBar,
    onUpdateProgressDialog,
  });

  return (
    <>
      <ShippingProvidersTable_v2
        refreshTableIndex={refreshTableIndex}
        onUpdateShippingProvider={(selectedRow) => {
          onUpdateDialogState(DialogName.ManageShippingProvider, {
            data: selectedRow,
            open: true,
            errors: [],
          });
        }}
        onCreateShippingProvider={() => {
          onUpdateDialogState(DialogName.ManageShippingProvider, {
            data: null,
            open: true,
            errors: [],
          });
        }}
      />

      {dialog?.manageShippingProvider?.open && shippingProviderKeys && (
        <ManageShippingProviderDialog
          shippingProviderKeys={shippingProviderKeys}
          shippingProvider={dialog?.manageShippingProvider.data}
          errors={dialog?.manageShippingProvider.errors}
          onClose={() => {
            onUpdateDialogState(DialogName.ManageShippingProvider, {
              open: false,
            });
          }}
          proceed={(provider, action, providerId) => {
            if (action === 'add') {
              void handleCreateShippingProvider(provider);
            } else {
              void handleUpdateShippingProvider(provider, providerId);
            }
          }}
        />
      )}

      <WaveUINotificationBar
        message={notificationBar.message}
        severity={notificationBar.severity}
        open={notificationBar.open}
        close={() => onUpdateNotificationBar({ open: false })}
      />

      <WaveUIProgressDialog
        title={progressDialog?.title}
        progress={progressDialog?.progress || 0}
        open={progressDialog?.open || false}
      />
    </>
  );
};

export default ShippingProviders;
